import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { Call, Device } from '@twilio/voice-sdk'
import { getDevice } from '@/lib/twilioDeviceSingleton'
import { getPdDevice } from '@/lib/twilioPdDeviceSingleton'
import { AppelTwilio } from '@/lib/voix/appelTwilio'
import { surAppelEntrantTelnyx } from '@/lib/voix/telnyx'
import type { AppelAgent } from '@/lib/voix/types'
import { attenteParLeServeur, useCallStore } from '@/stores/useCallStore'
import { api } from '@/lib/api'
import { startCallDurationTimer, stopCallDurationTimer } from '@/lib/callTimer'
import { notifyDesktop } from '@/lib/desktopShell'
import { userIdDeIdentite } from '@/lib/identiteAgent'
import { ouvrirCollegue } from '@/lib/ouvrirConversation'
import type { TeamMember } from '@/api/agents'

/**
 * Hook qui s'abonne aux appels entrants des téléphones de l'agent (Devices
 * Twilio main + PD, téléphone Telnyx) et synchronise l'état avec useCallStore.
 *
 * Le PD Device (subaccount) reçoit les rappels de prospects qui appellent
 * les numéros Power Dialer. On utilise le MÊME handler pour les 2 Devices —
 * l'UI ne distingue pas selon l'origine, c'est juste un appel entrant.
 *
 * À monter UNE fois (dans VoiceProvider, après useTwilioDevice).
 */
export function useIncomingCall() {
  const queryClient = useQueryClient()

  useEffect(() => {
    const surAppel = makeIncomingHandler(queryClient)
    const handler = (call: Call) => surAppel(new AppelTwilio(call))
    const attached = { main: false, pd: false }
    // Le téléphone Telnyx publie ses appels entrants lui-même, connecté ou pas encore.
    const detacherTelnyx = surAppelEntrantTelnyx(surAppel)

    const tryAttach = () => {
      if (!attached.main) {
        const d = getDevice()
        if (d) {
          d.on('incoming', handler)
          attached.main = true
          console.info('[useIncomingCall] incoming listener attached (main)')
        }
      }
      if (!attached.pd) {
        const d = getPdDevice()
        if (d) {
          d.on('incoming', handler)
          attached.pd = true
          console.info('[useIncomingCall] incoming listener attached (PD)')
        }
      }
    }

    // Tentative immédiate puis retry périodique : les 2 Devices peuvent arriver
    // à des moments différents (PD apparaît seulement en session Power Dialer).
    tryAttach()
    const intervalId = setInterval(tryAttach, 500)

    return () => {
      clearInterval(intervalId)
      if (attached.main) getDevice()?.off('incoming', handler)
      if (attached.pd) getPdDevice()?.off('incoming', handler)
      detacherTelnyx()
      stopCallDurationTimer()
    }
  }, [queryClient])
}

function makeIncomingHandler(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  return async (call: AppelAgent) => {
    const callerNumber = call.de
    const store = useCallStore.getState()

    console.info('[useIncomingCall] incoming', {
      from: callerNumber,
      isOnHold: store.isOnHold,
      hadCurrentCall: !!store.currentCall,
    })

    // Cas spécial : reconnect après unhold (l'appel revient comme nouvel incoming)
    // (Twilio seulement : une attente native garde le même appel, et un
    // entrant pendant l'attente est un autre appel, à ne pas décrocher.)
    if (attenteParLeServeur()) {
      console.info('[useIncomingCall] auto-accepting unhold reconnect')
      call.on('accept', () => {
        console.info('[useIncomingCall] unhold reconnect accepted')
        // Le timer continue depuis sa valeur actuelle (on ne reset PAS callDuration)
        useCallStore.setState({
          currentCall: call,
          isCallConnected: true,
          isOnHold: false,
          isHoldLoading: false,
          retourAttendu: false,
        })
      })
      call.on('disconnect', () => {
        if (!useCallStore.getState().isOnHold) {
          stopCallDurationTimer()
          useCallStore.getState().reset()
          queryClient.invalidateQueries({ queryKey: ['call-logs'] })
        }
        call.removeAllListeners()
      })
      call.on('error', (err) => {
        console.error('[useIncomingCall] unhold reconnect error:', err)
        useCallStore.setState({
          isOnHold: false,
          isHoldLoading: false,
          retourAttendu: false,
        })
        useCallStore.getState().reset()
      })
      try {
        // call.accept() est synchrone côté Twilio Voice SDK v2 (retourne le Call)
        call.accept()
      } catch (err) {
        console.error('[useIncomingCall] call.accept() failed:', err)
        useCallStore.setState({ isOnHold: false, isHoldLoading: false, retourAttendu: false })
        useCallStore.getState().reset()
      }
      return
    }

    // Nouvel appel entrant standard
    useCallStore.setState({
      currentCall: call,
      callerPhoneNumber: callerNumber || '',
      callerContactName: '',
      isInternalCall: callerNumber?.startsWith('client:agent_') || false,
      callDirection: 'inbound',
      showGlobalIncomingCall: true,
      isCallConnected: false,
    })

    notifyDesktop('incoming_call', {
      from: callerNumber || '',
      callSid: call.id || '',
    })

    // Appel d'un collègue : son nom vient de l'équipe, jamais de l'identité brute.
    const collegueId = userIdDeIdentite(callerNumber)
    if (collegueId) {
      const membre = (queryClient.getQueryData<TeamMember[]>(['team-members']) || []).find(
        (m) => m.user_id === collegueId,
      )
      const nom = membre ? `${membre.user_first_name || ''} ${membre.user_last_name || ''}`.trim() || membre.user_email : ''
      if (nom) useCallStore.getState().setCallerContactName(nom)
    }

    // Récupérer le nom du contact en parallèle (best-effort)
    if (callerNumber && !collegueId) {
      try {
        const { data } = await api.get<{ first_name?: string; last_name?: string }>(
          `/api/contacts/${encodeURIComponent(callerNumber)}/`,
        )
        if (data && (data.first_name || data.last_name)) {
          useCallStore
            .getState()
            .setCallerContactName(
              `${data.first_name || ''} ${data.last_name || ''}`.trim(),
            )
        }
      } catch {
        // Pas de contact trouvé, OK
      }
    }

    const isMobile = window.innerWidth < 768

    const onAccept = () => {
      startCallDurationTimer()
      useCallStore.setState({ isCallConnected: true })

      if (!isMobile) {
        // Sur PC, masquer la modale et ouvrir la conversation
        useCallStore.setState({ showGlobalIncomingCall: false })
        if (collegueId) {
          ouvrirCollegue(collegueId)
        } else if (callerNumber) {
          // Event découplé : le dashboard écoute pour ouvrir la conversation
          window.dispatchEvent(
            new CustomEvent('callizy:open-conversation', {
              detail: { phone: callerNumber },
            }),
          )
        }
      }
    }

    const onTerminated = () => {
      stopCallDurationTimer()
      useCallStore.getState().reset()
      // Le SSE invalidera ['call-logs'] de toute façon, mais on force ici
      // pour une UX immédiate
      queryClient.invalidateQueries({ queryKey: ['call-logs'] })
      notifyDesktop('call_ended')
      call.removeAllListeners()
    }

    const onCancel = () => {
      stopCallDurationTimer()
      const wasConnected = useCallStore.getState().isCallConnected
      if (!wasConnected) {
        useCallStore.getState().reset()
        notifyDesktop('missed_call', { from: callerNumber || '' })
      } else {
        notifyDesktop('call_ended')
      }
      call.removeAllListeners()
    }

    const onDisconnect = () => {
      if (attenteParLeServeur()) return
      stopCallDurationTimer()
      useCallStore.getState().reset()
      queryClient.invalidateQueries({ queryKey: ['call-logs'] })
      notifyDesktop('call_ended')
      call.removeAllListeners()
    }

    call.on('accept', onAccept)
    call.on('reject', onTerminated)
    call.on('cancel', onCancel)
    call.on('disconnect', onDisconnect)
    call.on('error', (err) => {
      console.error('[useIncomingCall] error:', err)
      // Pendant un hold, le call leg de l'agent peut renvoyer une erreur — on
      // ignore pour ne pas perdre l'écran. L'unhold rétablira la connexion.
      if (attenteParLeServeur()) return
      stopCallDurationTimer()
      useCallStore.getState().reset()
      call.removeAllListeners()
    })
  }
}

// Helper export pour PD device qui réutilise le même handler
export function attachIncomingHandlerToDevice(
  device: Device,
  queryClient: ReturnType<typeof useQueryClient>,
) {
  const surAppel = makeIncomingHandler(queryClient)
  const handler = (call: Call) => surAppel(new AppelTwilio(call))
  device.on('incoming', handler)
  return () => device.off('incoming', handler)
}
